/**
 * Branch Web SDK - Checkout Tracking
 * Tracks INITIATE_CHECKOUT events when user clicks checkout button
 *
 * Dependencies: branch-utils.js (must be loaded first)
 */

(function() {
  'use strict';

  // Check if utilities are loaded
  if (!window.BranchUtils) {
    console.error('BranchUtils not found. Load branch-utils.js first!');
    return;
  }

  /**
   * Get current cart data from Shopify
   * @returns {Promise<Object>} Cart data with total and items
   */
  function getCurrentCart() {
    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (window.BranchConfig.debug) {
          console.log('💳 Cart data fetched:', cart);
        }

        return {
          total: window.BranchUtils.formatPrice(cart.total_price || 0),
          items: cart.items || [],
          item_count: cart.item_count || 0,
          currency: cart.currency || window.BranchConfig.currency
        };
      })
      .catch(error => {
        console.error('Failed to fetch cart:', error);
        return {
          total: 0,
          items: [],
          item_count: 0,
          currency: window.BranchConfig.currency
        };
      });
  }

  /**
   * Track INITIATE_CHECKOUT event
   * @param {Object} cartData - Cart data from Shopify
   */
  function trackInitiateCheckout(cartData) {
    // Wait for Branch SDK to be ready
    window.BranchUtils.whenBranchReady(function() {
      const eventData = {
        transaction_id: `checkout_${Date.now()}`,
        revenue: cartData.total || 0,
        currency: cartData.currency || window.BranchConfig.currency,
        shipping: 0,
        tax: 0,
        affiliation: 'Shopify Store'
      };

      // Add cart items if available
      if (cartData.items && cartData.items.length > 0) {
        eventData.content_items = cartData.items.map(item =>
          window.BranchUtils.createCommerceItem(item)
        );
      }

      // Track the event
      window.BranchUtils.trackEvent('INITIATE_PURCHASE', eventData);
    });
  }

  // Debounced version to prevent multiple clicks
  const debouncedInitiateCheckout = window.BranchUtils.debounce(trackInitiateCheckout, 500);

  /**
   * Initialize checkout button tracking
   * Finds all checkout buttons and adds click listeners
   */
  function initCheckoutTracking() {
    // Common selectors for checkout buttons across Shopify themes
    const checkoutSelectors = [
      'button[name="checkout"]',
      'a[href="/checkout"]',
      'a[href*="/checkout"]',
      'a[href*="/checkouts"]',
      '.cart__checkout',
      '.cart__ctas button',          // YOUR CART CTA CLASS
      '.cart__ctas a',                // YOUR CART CTA CLASS (link)
      '.cart__ctas [href*="checkout"]', // Specific checkout in ctas
      '.cart-drawer__checkout',
      '[data-checkout-button]',
      '#checkout-button',
      '.checkout-button',
      'button[type="submit"][name="checkout"]',
      // Cart drawer specific selectors
      'cart-drawer a[href*="/checkout"]',
      'cart-drawer button[type="submit"]',
      'cart-drawer .button--primary',
      'cart-drawer [class*="checkout"]',
      'cart-drawer form button',
      'cart-drawer .cart__ctas button',  // Cart CTAs in drawer
      'cart-drawer .cart__ctas a',       // Cart CTAs link in drawer
      '.drawer a[href*="/checkout"]',
      '.drawer button[type="submit"]',
      '.drawer .cart__ctas button',      // Drawer CTAs
      '.drawer .cart__ctas a',           // Drawer CTAs link
      // Generic button that might be checkout
      'form[action="/cart"] button[type="submit"]',
      'form[action*="checkout"] button',
      // ID-based selectors
      '#CartDrawer-Checkout',
      '#cart-drawer-checkout',
      // Class-based variations
      '.cart-drawer__footer button',
      '.cart-drawer__footer a',
      '[class*="CartDrawer"] [class*="Checkout"]'
    ];

    let trackersAdded = 0;

    checkoutSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(button => {
        // Only add listener if not already tracked
        if (!button.hasAttribute('data-branch-tracked')) {
          button.setAttribute('data-branch-tracked', 'true');

          button.addEventListener('click', function(e) {
            if (window.BranchConfig.debug) {
              console.log('💳 Checkout button clicked:', button);
            }

            // Fetch current cart and track checkout initiation
            getCurrentCart().then(cartData => {
              debouncedInitiateCheckout(cartData);
            });
          });

          trackersAdded++;
        }
      });
    });

    // Fallback: Search for checkout buttons by text content
    const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('.drawer');
    if (cartDrawer) {
      const allButtons = cartDrawer.querySelectorAll('button, a');
      allButtons.forEach(button => {
        if (button.hasAttribute('data-branch-tracked')) return;

        const text = (button.innerText || button.textContent || '').toLowerCase();
        const isCheckoutButton =
          text.includes('checkout') ||
          text.includes('check out') ||
          button.className.toLowerCase().includes('checkout') ||
          button.href?.includes('checkout') ||
          button.href?.includes('checkouts');

        if (isCheckoutButton) {
          button.setAttribute('data-branch-tracked', 'true');

          button.addEventListener('click', function(e) {
            if (window.BranchConfig.debug) {
              console.log('💳 Checkout button clicked (fallback):', button);
            }

            getCurrentCart().then(cartData => {
              debouncedInitiateCheckout(cartData);
            });
          });

          trackersAdded++;
        }
      });
    }

    if (window.BranchConfig.debug) {
      console.log(`💳 Added ${trackersAdded} checkout button tracker(s)`);
      if (trackersAdded === 0) {
        console.warn('⚠️ No checkout buttons found! Check selectors.');
      }
    }
  }

  /**
   * Initialize on page load
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initCheckoutTracking);
    } else {
      initCheckoutTracking();
    }

    // Force re-init after delays to catch dynamically loaded buttons
    setTimeout(initCheckoutTracking, 500);
    setTimeout(initCheckoutTracking, 1000);
    setTimeout(initCheckoutTracking, 2000);

    // Re-initialize when cart drawer or modals are opened (for dynamic content)
    // Use debounce to avoid excessive re-initialization
    const debouncedInit = window.BranchUtils.debounce(initCheckoutTracking, 300);

    const observer = new MutationObserver(function(mutations) {
      // Check if any relevant elements were added or attributes changed
      let shouldReinit = false;

      mutations.forEach(mutation => {
        // Check added nodes
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if it's a cart drawer or contains checkout buttons
              if (
                node.classList?.contains('cart-drawer') ||
                node.classList?.contains('cart-popup') ||
                node.id === 'CartDrawer-Checkout' ||
                node.classList?.contains('checkout-initiat') ||
                node.querySelector?.('[name="checkout"]') ||
                node.querySelector?.('[href*="/checkout"]') ||
                node.querySelector?.('#CartDrawer-Checkout') ||
                node.querySelector?.('.checkout-initiat')
              ) {
                shouldReinit = true;
              }
            }
          });
        }

        // Check attribute changes (like style="display:none" to "display:block")
        if (mutation.type === 'attributes' && mutation.target.nodeType === 1) {
          const target = mutation.target;
          if (
            target.id === 'CartDrawer-Checkout' ||
            target.classList?.contains('checkout-initiat') ||
            target.classList?.contains('cart-drawer') ||
            (mutation.attributeName === 'style' && target.querySelector?.('#CartDrawer-Checkout'))
          ) {
            shouldReinit = true;
          }
        }
      });

      if (shouldReinit) {
        debouncedInit();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,        // Watch for attribute changes
      attributeFilter: ['style', 'class'] // Only watch style and class changes
    });
  }

  // Start initialization
  init();

  // Debug log
  if (window.BranchConfig.debug) {
    console.log('💳 Branch Checkout Tracking initialized');
  }

})();
